<template>
  <div ref="rootRef" :dir="isRtl !== false ? 'rtl' : 'ltr'" :class="['relative custom-selector-container', widthClass]">
    <button
      type="button"
      :class="['btn relative justify-start font-normal h-auto py-2 px-3 min-h-0', widthClass, isRtl === true ? 'text-right' : 'text-left']"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen !== false"
    >
      <div v-if="selectedOptionObj !== null" class="flex items-center w-full min-w-0" style="padding-inline-end: 1.75rem;">
        <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
          <span class="block text-sm font-medium truncate relative w-full">{{ formatOptionLabel(selectedOptionObj) }}</span>
        </div>
        <span v-if="showFlagOnRight === true && getOptionFlag(selectedOptionObj) !== ''" class="text-base leading-none flex-shrink-0" style="margin-inline-start: 0.5rem;">{{ getOptionFlag(selectedOptionObj) }}</span>
      </div>
      <div v-else class="flex items-center w-full min-w-0" style="padding-inline-end: 1.75rem;">
        <span class="text-sm text-base-content opacity-70">{{ placeholder }}</span>
      </div>
      <Icon icon="heroicons:chevron-down-20-solid" class="pointer-events-none absolute top-1/2 h-5 w-5 -translate-y-1/2 text-base-content opacity-60" :class="isRtl === true ? 'left-3' : 'right-3'" />
    </button>

    <Teleport to="body">
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-if="isOpen === true"
          ref="dropdownListRef"
          class="flex max-h-full min-w-0 flex-col overflow-hidden rounded-md border border-base-300 bg-base-100 shadow-lg dark:border-gray-700 dark:bg-gray-800"
          :style="dropdownPanelStyle"
        >
        <div v-if="showSearch === true" class="border-b border-base-300 px-3 py-3 dark:border-gray-700">
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="input input-bordered h-10 w-full"
            :class="isRtl === true ? 'text-right' : 'text-left'"
            :placeholder="t('aiChatAgentSetup.voiceSettings.languageSearchPlaceholder', 'Search language')"
          />
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
          <div
            v-for="opt in filteredOptions"
            :key="getOptionValue(opt)"
            class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{ 'bg-primary text-primary-content': getOptionValue(opt) === modelValue, 'hover:bg-base-200 dark:hover:bg-gray-700': getOptionValue(opt) !== modelValue }"
            @click="selectOptionAndClose(getOptionValue(opt))"
          >
            <div class="p-3">
              <div class="flex items-start w-full">
                <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'">
                  <h3 class="text-sm font-semibold leading-tight" :class="[{ 'text-primary-content': getOptionValue(opt) === modelValue }, isRtl === true ? 'text-right' : 'text-left']" :style="isRtl === true ? 'padding-right:2%;' : ''">{{ formatOptionLabel(opt) }}</h3>
                </div>
                <span v-if="showFlagOnRight === true && getOptionFlag(opt) !== ''" class="text-base leading-none flex-shrink-0" :class="isRtl === true ? 'mr-2' : 'ml-2'">{{ getOptionFlag(opt) }}</span>
              </div>
            </div>
          </div>
          <div v-if="filteredOptions.length === 0" class="px-3 py-4 text-sm text-base-content opacity-60">
            {{ t('voiceSelector.noResults', 'No languages matched your search.') }}
          </div>
        </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import { useBodyAnchoredDropdown } from '../composables/useBodyAnchoredDropdown.js';
import { getLanguageFlag } from '../utils/geminiLanguages.js';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Select a Language' },
  options: { type: Array, default: () => [] },
  widthClass: { type: String, default: 'w-full' },
  showFlagOnRight: { type: Boolean, default: false }
});
const emit = defineEmits(['update:modelValue']);

const { currentLanguage, t } = useI18n();
const isRtl = computed(() => (['he', 'ar'].includes(currentLanguage.value) === true) ? true : false);

const isOpen = ref(false);
const rootRef = ref(null);
const dropdownListRef = ref(null);
const searchInputRef = ref(null);
const searchQuery = ref('');
const { panelStyle: dropdownPanelStyle } = useBodyAnchoredDropdown(rootRef, isOpen, { capHeight: 320, zIndex: 265 });

function getOptionValue(option) {
  if (option !== null && typeof option === 'object') {
    return option.code || option.id || option.value || '';
  }
  return option;
}

function getOptionLabel(option) {
  if (option !== null && typeof option === 'object') {
    return option.name || option.label || option.code || option.id || option.value || '';
  }
  return option;
}

function getOptionFlag(option) {
  const label = getOptionLabel(option);
  const value = getOptionValue(option);
  return getLanguageFlag(label, value);
}

function formatOptionLabel(option) {
  const label = getOptionLabel(option);
  if (props.showFlagOnRight === true) {
    return label;
  }
  const flag = getOptionFlag(option);
  if (flag === '') {
    return label;
  }
  return `${flag} ${label}`;
}

const selectedOptionObj = computed(() => {
  const found = props.options.find(opt => getOptionValue(opt) === props.modelValue);
  return found !== undefined ? found : null;
});

const showSearch = computed(() => props.options.length > 8);
const filteredOptions = computed(() => {
  const query = typeof searchQuery.value === 'string' ? searchQuery.value.trim().toLowerCase() : '';
  if (query === '') {
    return props.options;
  }
  return props.options.filter((option) => {
    const label = String(getOptionLabel(option) || '').toLowerCase();
    const value = String(getOptionValue(option) || '').toLowerCase();
    return label.includes(query) === true || value.includes(query) === true;
  });
});

const toggleDropdown = () => { isOpen.value = isOpen.value !== true; };
const selectOptionAndClose = (opt) => {
  if (props.modelValue !== opt) {
    emit('update:modelValue', opt);
  }
  searchQuery.value = '';
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  if (isOpen.value !== true) {
    return;
  }
  if (rootRef.value !== null && rootRef.value.contains(event.target) === true) {
    return;
  }
  if (dropdownListRef.value !== null && dropdownListRef.value.contains(event.target) === true) {
    return;
  }
  isOpen.value = false;
};

watch(isOpen, (nextValue) => {
  if (nextValue !== true) {
    searchQuery.value = '';
    return;
  }
  nextTick(() => {
    if (showSearch.value === true && searchInputRef.value !== null) {
      searchInputRef.value.focus();
    }
  });
});

onMounted(() => { document.addEventListener('click', handleClickOutside, true); });
onBeforeUnmount(() => { document.removeEventListener('click', handleClickOutside, true); });
</script>

